/* eslint-disable react-hooks/rules-of-hooks */
import * as React from "react"
import { Eye } from 'lucide-react';
import { cn } from "../lib/utils"
import { EyeOff } from 'lucide-react';
interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string
  topLabel?: string
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  variant?: "standard" | "outlined" | "filled"
  Size?: "default" | "sm" | "lg" | "icon"
  error?: boolean
  errorText?: string
}

function Input({
  topLabel,
  label,
  className,
  type = "text",
  variant = "outlined",
  Size = "default",
  startIcon,
  endIcon,
  error = false,
  errorText,
  id,
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  ...props
}: InputProps) {
  // State for password visibility toggle
  const [showPassword, setShowPassword] = React.useState(false)
  const actualType = type === "password" && showPassword ? "text" : type
  
  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev)
  }
  const inputId = id || React.useId()
  const describedById = errorText ? `${inputId}-error-text` : undefined

  const [focused, setFocused] = React.useState(false)
  const [hasValue, setHasValue] = React.useState(!!defaultValue || !!value)
  const [keyboardFocus, setKeyboardFocus] = React.useState(false)

  const [lastInteractionWasKeyboard, setLastInteractionWasKeyboard] = React.useState(false)

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        setLastInteractionWasKeyboard(true)
      }
    }

    const handleMouseDown = () => {
      setLastInteractionWasKeyboard(false)
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('mousedown', handleMouseDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('mousedown', handleMouseDown)
    }
  }, [])

  // A caller's onFocus/onBlur used to land in `...props` after these and
  // replace them, so the focus styling never cleared. Both run now.
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true)
    setKeyboardFocus(lastInteractionWasKeyboard)
    onFocus?.(e)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false)
    setKeyboardFocus(false)
    setHasValue(!!e.target.value)
    onBlur?.(e)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasValue(!!e.target.value)
    onChange?.(e)
  }

  const variantClasses = {
    outlined: "bg-transparent border border-stroke-500 rounded-sm",
    filled: "border border-stroke-500 rounded-sm bg-secondary-100/50",
    standard: "border-0 border-b border-stroke-500 bg-transparent focus-visible:ring-0 shadow-none",
  }

  const sizeClasses = {
    default: "h-10 px-3",
    sm: "h-9 px-2 text-sm",
    lg: "h-12 px-4 text-lg",
    icon: "size-9",
  }

  const baseBorderClass = error
    ? "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20"
    : "border-stroke-500"
  const getHoverFocusBorderClass = () => {
    if (variant === "standard") {
      return error
        ? "hover:border-b-destructive hover:border-b-[1px] focus:border-b-destructive focus:border-b-[1px]"
        : "hover:border-b-primary hover:border-b-[1px] focus:border-b-primary focus:border-b-[1px]"
    } else {
      return error
        ? "hover:border-destructive hover:border-[1px] focus:border-destructive focus:border-[1px]"
        : "hover:border-primary hover:border-[1px] focus:border-primary focus:border-[1px]"
    }
  }

  const borderClass = `${baseBorderClass} ${getHoverFocusBorderClass()}`
  const shouldFloatLabel = focused || hasValue

  return (
    <div className="relative w-full space-y-1.5">
      {topLabel && (
        <div className="text-text-100 mb-2 font-light text-xs">
          {topLabel}
        </div>
      )}

      <div className={cn("relative", label && " pb-2")}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              "absolute left-3 transition-all duration-200 z-10 px-1 bg-background",
              error ? "text-destructive" : "text-muted-foreground",
              "pointer-events-none",
              shouldFloatLabel 
  ? `-top-2 text-xs ${error ? 'text-error-500' : 'text-primary'}` 
  : 'top-2.5 text-sm'
            )}
          >
            {label}
          </label>
        )}

        {startIcon && (
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground dark:text-muted-foreground/80 pointer-events-none group-hover:text-foreground group-focus-within:text-primary transition-colors duration-200">
            {startIcon}
          </span>
        )}

        <input
          id={inputId}
          type={actualType}
          aria-invalid={error}
          aria-describedby={describedById}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          value={value}
          defaultValue={defaultValue}
          placeholder={label ? " " : props.placeholder}
          className={cn(
            "group peer selection:bg-primary selection:text-primary-foreground flex w-full min-w-0 text-base shadow-xs transition-all duration-200 outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            /* Date/time input styling */
            "[&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:dark:invert [&::-webkit-calendar-picker-indicator]:dark:brightness-200 [&::-webkit-calendar-picker-indicator]:dark:saturate-0 [&::-webkit-calendar-picker-indicator]:dark:filter",
            /* File input styling */
            "file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
            variantClasses[variant],
            borderClass,
            sizeClasses[Size],

            // Regular focus styles (mouse click)
            !error && focused && !keyboardFocus && variant !== "standard" && "ring-[0px] ring-secondary/50",
            !error && focused && !keyboardFocus && variant === "standard" && "border-b-[1px] border-b-secondary",
            error && focused && !keyboardFocus && variant !== "standard" && "ring-[0px] ring-error-500",
            error && focused && !keyboardFocus && variant === "standard" && "border-b-[1px] border-b-error-500",
            
            // Keyboard focus styles (tab key)
            !error && keyboardFocus && variant !== "standard" && "ring-[3px] ring-secondary",
            !error && keyboardFocus && variant === "standard" && "!border !border-secondary !rounded-sm !ring-[3px] !ring-secondary",
            error && keyboardFocus && variant !== "standard" && "ring-[3px] ring-secondary-500/50",
            error && keyboardFocus && variant === "standard" && "!border !border-secondary-500/50 !rounded-sm !ring-[3px] !ring-secondary-500/50",

            startIcon && "pl-8",
            (endIcon || type === "password") && "pr-10",
            label && "placeholder-transparent",
            error && "aria-invalid:ring-error/20 dark:aria-invalid:ring-error/40",
            className
          )}
          {...props}
        />

        {type === "password" ? (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground dark:text-muted-foreground/80 cursor-pointer hover:text-foreground transition-colors duration-200"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
             <Eye className="h-5 w-5" />
            ) : (
              <EyeOff className="h-5 w-5" />
            )}
          </button>
        ) : endIcon ? (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground dark:text-muted-foreground/80 pointer-events-none group-hover:text-foreground group-focus-within:text-primary transition-colors duration-200">
            {endIcon}
          </span>
        ) : null}
      </div>

      {errorText && (
        <p id={describedById} className="text-xs text-destructive mt-1">
          {errorText}
        </p>
      )}
    </div>
  )
}

export { Input }
